import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { TrendingDown, Clock, Tag, ArrowUpRight } from "lucide-react";
import { getSoldArchive } from "@/lib/sold-archive.functions";

const BASE = "https://www.akhusbilfritid.se";
const URL = `${BASE}/salda`;

const soldArchiveQuery = queryOptions({
  queryKey: ["sold-archive"],
  queryFn: () => getSoldArchive(),
  staleTime: 30 * 60 * 1000,
});

const TITLE = "Sålda husbilar & husvagnar — riktiga slutpriser | AK Husbil";
const DESC =
  "Vad husbilar och husvagnar faktiskt såldes för hos AK Husbil & Fritid i Kalix — slutpris, liggtid och prisnivå per märke.";

export const Route = createFileRoute("/salda")({
  loader: ({ context }) => context.queryClient.ensureQueryData(soldArchiveQuery),
  head: ({ loaderData }) => {
    const total = loaderData?.total ?? 0;
    return {
      meta: [
        { title: TITLE },
        { name: "description", content: DESC },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
        { property: "og:type", content: "article" },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESC },
        { property: "og:url", content: URL },
      ],
      links: [{ rel: "canonical", href: URL }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Dataset",
            name: "Slutpriser på sålda husbilar och husvagnar hos AK Husbil & Fritid",
            description: DESC,
            url: URL,
            creator: { "@type": "AutoDealer", name: "AK Husbil & Fritid", url: BASE },
            temporalCoverage: "2026/..",
            variableMeasured: ["Slutpris", "Dagar till försäljning", "Märke", "Fordonstyp"],
            distribution: { "@type": "DataDownload", encodingFormat: "text/html", contentUrl: URL },
            ...(total ? { size: `${total} sålda fordon` } : {}),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: BASE + "/" },
              { "@type": "ListItem", position: 2, name: "Sålda fordon", item: URL },
            ],
          }),
        },
      ],
    };
  },
  component: SoldPage,
});

const sek = (n: number | null) =>
  n == null ? "–" : new Intl.NumberFormat("sv-SE").format(n) + " kr";

function SoldPage() {
  const { data } = useSuspenseQuery(soldArchiveQuery);

  return (
    <div className="bg-warm">
      <header className="border-b border-border bg-card/40">
        <div className="container-edge py-14 md:py-20">
          <p className="eyebrow">Originaldata från vårt eget lager</p>
          <h1 className="mt-2 max-w-3xl font-display text-3xl leading-tight md:text-5xl">
            Sålda husbilar och husvagnar — vad de faktiskt gick för
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            De flesta prisguider bygger på begärda priser. Den här sidan bygger på{" "}
            {data.total} fordon som verkligen har sålts hos oss i Kalix. Varje gång ett fordon
            försvinner ur vårt lager loggas pris, fordonstyp och hur många dagar det låg till
            försäljning. Uppdaterad {data.updatedAt}.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            <Stat icon={<Tag size={15} />} label="Sålda fordon i statistiken" value={String(data.total)} />
            <Stat
              icon={<TrendingDown size={15} />}
              label="Medianpris, alla fordon"
              value={sek(data.byCategory.length ? median(data.byCategory) : null)}
            />
            <Stat
              icon={<Clock size={15} />}
              label="Snitt dagar till försäljning"
              value={
                data.byCategory.length
                  ? `${Math.round(
                      data.byCategory.reduce((a, s) => a + (s.avgDays ?? 0) * s.count, 0) /
                        Math.max(1, data.byCategory.reduce((a, s) => a + s.count, 0)),
                    )} dagar`
                  : "–"
              }
            />
          </div>
        </div>
      </header>

      <section className="container-edge py-14">
        <h2 className="font-display text-2xl md:text-3xl">Prisnivå per fordonstyp</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Medianen säger mer än snittet när enstaka dyra fordon drar upp siffran. Båda visas här.
        </p>
        <SegmentTable
          rows={data.byCategory}
          firstHeader="Fordonstyp"
          empty="Ingen statistik ännu."
        />
      </section>

      {data.byBrand.length > 0 && (
        <section className="container-edge pb-14">
          <h2 className="font-display text-2xl md:text-3xl">Prisnivå per märke</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Baserat på märkesnamnet i annonsen. Märken med få sålda exemplar ger osäkrare siffror —
            antalet står i tabellen så du kan bedöma det själv.
          </p>
          <SegmentTable rows={data.byBrand} firstHeader="Märke" empty="—" />
        </section>
      )}

      {data.byPriceBand.length > 0 && (
        <section className="container-edge pb-14">
          <h2 className="font-display text-2xl md:text-3xl">Hur snabbt säljs olika prisklasser?</h2>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Det här är den siffra som säljare frågar oss om mest: hur lång tid tar det? Dyra fordon
            har en mindre köpargrupp och ligger i regel längre.
          </p>
          <SegmentTable rows={data.byPriceBand} firstHeader="Prisklass" empty="—" />
        </section>
      )}

      {data.vehicles.length > 0 && (
        <section className="container-edge pb-14">
          <h2 className="font-display text-2xl md:text-3xl">Senast sålda fordon</h2>
          <div className="mt-6 overflow-x-auto rounded-3xl border border-border bg-card">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-3 font-medium">Fordon</th>
                  <th className="px-5 py-3 font-medium">Slutpris</th>
                  <th className="px-5 py-3 font-medium">Såld</th>
                  <th className="px-5 py-3 font-medium">Dagar till salu</th>
                </tr>
              </thead>
              <tbody>
                {data.vehicles.map((v, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="px-5 py-3 font-medium">
                      <Link
                        to="/salda/$slug"
                        params={{ slug: v.slug }}
                        className="inline-flex items-center gap-1.5 hover:text-accent"
                      >
                        {v.title}
                        <ArrowUpRight size={13} className="shrink-0 text-accent" />
                      </Link>
                    </td>
                    <td className="px-5 py-3">{sek(v.price)}</td>
                    <td className="px-5 py-3 text-muted-foreground">{v.soldAt}</td>
                    <td className="px-5 py-3 text-muted-foreground">
                      {v.daysOnMarket != null ? `${v.daysOnMarket} dagar` : "–"}
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="container-edge pb-20">
        <div className="rounded-3xl border border-border bg-card p-6 md:p-10">
          <h2 className="font-display text-2xl md:text-3xl">Så använder du siffrorna</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Next to="/husbilar-till-salu" label="Aktuella husbilar i lager" desc="Jämför dagens priser mot slutpriserna ovan." />
            <Next to="/husvagnar-till-salu" label="Aktuella husvagnar" desc="Samma sak för husvagnssidan." />
            <Next to="/verktyg/avskrivning-husbil" label="Räkna värdeminskning" desc="Se vad ditt fordon är värt om tre år." />
            <Next to="/verktyg/manadskostnad-husbil" label="Räkna månadskostnad" desc="Ränta, försäkring, förvaring och service." />
            <Next to="/guider/kopa-husbil-checklista" label="Checklista före köp" desc="Vad vi själva kontrollerar innan ett fordon säljs." />
            <Next to="/jamfor" label="Jämför modeller" desc="Direkta jämförelser mellan vanliga alternativ." />
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Metod: varje timme läser vi av vårt aktuella lager. Ett fordon som försvinner och inte
            återkommer inom 20 minuter räknas som sålt, och slutpriset är det pris som var publicerat
            när det försvann. Fordon som plockats bort av annan orsak än försäljning kan i enstaka
            fall räknas med — vi rensar manuellt när vi upptäcker det. Sidan uppdateras automatiskt.
          </p>
        </div>
      </section>
    </div>
  );
}

function median(segments: Array<{ medianPrice: number | null; count: number }>): number | null {
  const vals = segments.filter((s) => s.medianPrice != null);
  if (vals.length === 0) return null;
  const total = vals.reduce((a, s) => a + s.count, 0);
  return Math.round(vals.reduce((a, s) => a + (s.medianPrice as number) * s.count, 0) / Math.max(1, total));
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </p>
      <p className="mt-2 font-display text-2xl">{value}</p>
    </div>
  );
}

function SegmentTable({
  rows,
  firstHeader,
  empty,
}: {
  rows: Array<{ label: string; count: number; avgPrice: number | null; medianPrice: number | null; avgDays: number | null }>;
  firstHeader: string;
  empty: string;
}) {
  if (rows.length === 0) return <p className="mt-6 text-sm text-muted-foreground">{empty}</p>;
  return (
    <div className="mt-6 overflow-x-auto rounded-3xl border border-border bg-card">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="px-5 py-3 font-medium">{firstHeader}</th>
            <th className="px-5 py-3 font-medium">Antal sålda</th>
            <th className="px-5 py-3 font-medium">Medianpris</th>
            <th className="px-5 py-3 font-medium">Snittpris</th>
            <th className="px-5 py-3 font-medium">Snitt dagar</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-border/60">
              <td className="px-5 py-3 font-medium capitalize">{r.label}</td>
              <td className="px-5 py-3">{r.count}</td>
              <td className="px-5 py-3">{sek(r.medianPrice)}</td>
              <td className="px-5 py-3 text-muted-foreground">{sek(r.avgPrice)}</td>
              <td className="px-5 py-3 text-muted-foreground">
                {r.avgDays != null ? `${r.avgDays} dagar` : "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Next({ to, label, desc }: { to: string; label: string; desc: string }) {
  return (
    <Link
      to={to}
      className="group flex items-start justify-between gap-3 rounded-2xl border border-border bg-background p-5 transition hover:border-accent"
    >
      <span>
        <span className="block font-medium group-hover:text-accent">{label}</span>
        <span className="mt-1 block text-sm text-muted-foreground">{desc}</span>
      </span>
      <ArrowUpRight size={16} className="mt-1 shrink-0 text-accent" />
    </Link>
  );
}
